/**
 * Copyright © 2025 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import React from 'react';
import { Box, Typography } from '@mui/material';
import StatusChip from '../../../../components/StatusChip';
import { colors } from '@farmlands/mui-theme';
import TestIds from '../../../../testIds';
import { useProductStatus } from '../../utils/useProductStatus';
import { LifeCycleState } from '../../storage';
import { useSelector } from 'react-redux';
import { selectCurrentFarmerAccount } from '../../../azure-b2c/storage';

export const ProductLifeCycleStatus = ({
  lifeCycleState,
  basePriceExcGst,
}: {
  lifeCycleState: string | null;
  basePriceExcGst: number;
}) => {
  const currentAccount = useSelector(selectCurrentFarmerAccount);
  const isTfo = currentAccount?.hasTfoAccess;

  const { noPriceAvailable, isUnavailable, isDiscontinued, isEndOfLife } = useProductStatus(
    lifeCycleState,
    basePriceExcGst,
  );

  let label = '';
  let subtitle = '';

  if (noPriceAvailable || isUnavailable) label = 'Unavailable';
  if (isDiscontinued) label = 'Discontinued';
  if (isEndOfLife) label = 'End of product line';

  if (isTfo) {
    if (lifeCycleState === LifeCycleState.NOT_STOCKED) {
      label = 'Active not stocked';
    }

    if (lifeCycleState === LifeCycleState.TEMPORARILY_UNAVAILABLE) {
      label = 'Temporarily unavailable';
    }
    // Shareholders
  } else if (lifeCycleState === LifeCycleState.TEMPORARILY_UNAVAILABLE) {
    label = 'Temporarily unavailable';
    subtitle = 'We will contact you for fulfilment options.';
  }

  return ProductLifeCycleStatusUI({ label, subtitle });
};

export const ProductLifeCycleStatusUI = ({
  label,
  subtitle,
}: {
  label?: string;
  subtitle?: string;
}) => {
  if (!label && !subtitle) return;

  return (
    <Box>
      <StatusChip
        label={label}
        variant='filled'
        bgColor={colors.grey400}
        textColor={colors.grey900}
        data-testid={TestIds.feat.product.card.statusBadge}
        sx={{
          textTransform: 'none',
          fontSize: '16px',
          lineHeight: '24px',
          height: 'auto',
          borderRadius: '32px',
          padding: '0px',
        }}
      />
      {subtitle && <Typography variant='body2'>{subtitle}</Typography>}
    </Box>
  );
};
